/* eslint-disable array-callback-return */
import { useCallback, useEffect, useMemo } from 'react';
import { useQueryClient } from 'react-query';
import { useParams, useSearchParams } from 'react-router-dom';

import { GoBack, GoNext, Loading, useSearchQuery } from '@catalyze/ui';
import { updateEDM } from 'api/edm/updateEdm';
import { useGetLeadQuery } from 'api/leads/getLead';
import { resetLead } from 'api/leads/resetLead';
import { updateLead } from 'api/leads/updateLead';
import { EdmStatus } from 'api/types/edm';
import type { ILead } from 'api/types/lead';
import { Status } from 'api/types/lead';
import { useInfiniteTableData } from 'components/Table/helpers/useInfiniteTableData';
import { Routes } from 'constants/routes';
import type { SortOrder } from 'constants/sort';
import { useFilterContext } from 'features/RealEstate/context/FilterContext';
import { usePropertyUrl } from 'features/RealEstate/utils/usePropertyUrl';

import { Container, PropertyGrid, StyledNav } from './styled';

import { ExpectedDecisionMaker } from '../../components/ExpectedDecisionMakers';
import { PropertyInfo } from '../../components/PropertyInfo';
import { TimelineContainer } from '../../components/TimelineContainer';

type IPropertyDetailParams = {
    propertyId?: string;
};

const getRouteFromStatus = (status: Status) => {
    switch (status) {
        case Status.ACTIVE:
            return Routes.ACTIVE_CONVERSATIONS;
        case Status.ARCHIVED:
            return Routes.ARCHIVE;
        case Status.CONTACTED:
            return Routes.CONTACTED;
        case Status.LISTED:
            return Routes.LISTED;
        case Status.RECOMMENDED:
            return Routes.RECOMMENDED;
        case Status.ALL:
            return Routes.SEARCH;
        case Status.SOLD:
            return Routes.SOLD;
        default:
            return undefined;
    }
};

export const PropertyDetail = () => {
    const queryClient = useQueryClient();
    const { propertyId } = useParams<IPropertyDetailParams>();
    const [searchParams] = useSearchParams();
    const searchTerm = useSearchQuery();
    const { filterState } = useFilterContext();

    const { data, isLoading } = useGetLeadQuery({
        id: Number(propertyId),
        enabled: Boolean(propertyId),
    });

    const sortBy = searchParams.get('sortBy');
    const sortOrder = searchParams.get('sortOrder') as SortOrder;
    const status = searchParams.get('status') as Status;

    const originRoute = getRouteFromStatus(status);
    const filtersFormData = originRoute ? filterState[originRoute] : undefined;

    const navigateToProperty = usePropertyUrl({
        sortBy,
        sortOrder,
        status,
        // eslint-disable-next-line id-length
        q: searchTerm,
    });
    const { ...tableProps } = useInfiniteTableData({
        // apparently, the property-leads query does not like seeing "status": "All" in the request
        status: status === Status.ALL ? undefined : status ?? undefined,
        search: searchTerm ?? undefined,
        sortParams: {
            sortBy: sortBy ?? undefined,
            sortOrder: sortOrder ?? undefined,
        },
        filters: filtersFormData,
    });

    const handleEdmUpdate = async () => {
        await queryClient.invalidateQueries('property-lead');
        await queryClient.invalidateQueries('infinitePropertyLeads');
        await queryClient.invalidateQueries('property-leads');
    };

    const reset = async (id: number) => {
        if (data) {
            const newEdms: ILead = { ...data };
            newEdms.edms.map((el) => {
                el.status = EdmStatus.NEW;
                for (const email of el.emails) {
                    if (email.updatedAt) delete email.updatedAt;
                }
                for (const phone of el.phones) {
                    if (phone.updatedAt) delete phone.updatedAt;
                }
                for (const address of el.addresses) {
                    if (address.updatedAt) delete address.updatedAt;
                }
            });

            const promises = [];
            for (const edm of newEdms.edms) {
                promises.push(updateEDM({ id: edm?.edmId, data: edm }));
            }

            // @ts-expect-error
            await Promise.all([
                resetLead({
                    id,
                }),
                ...promises,
            ]);
            await handleEdmUpdate();
        }
    };

    const updateLeadStatus = useCallback(async (status: Status, id: number) => {
        await updateLead({
            id,
            data: {
                status,
            },
        });
        void queryClient.invalidateQueries('infinitePropertyLeads');
        void queryClient.invalidateQueries('property-lead');
        void queryClient.invalidateQueries('property-leads');
        void queryClient.invalidateQueries('property-leads-worth');
    }, []);

    const nextProperty = useMemo(() => {
        if (!status || !tableProps || !tableProps.tableData) {
            return null;
        }

        const { tableData } = tableProps;
        const propertyLeadId = parseInt(propertyId ?? '', 10);
        let index = tableData.findIndex(
            (property) => property.propertyLeadId === propertyLeadId
        );

        return ++index < tableData.length ? tableData[index] : null;
    }, [propertyId, status, tableProps]);

    // try to load more properties if need be
    useEffect(() => {
        if (!tableProps || !tableProps.onReachedEnd) {
            return;
        }

        const { onReachedEnd } = tableProps;
        if (!nextProperty) {
            onReachedEnd();
        }
    }, [nextProperty, tableProps]);

    return (
        <Container>
            <StyledNav>
                <GoBack />
                {nextProperty && (
                    <GoNext
                        onClickHandler={navigateToProperty(
                            nextProperty.propertyLeadId
                        )}
                    />
                )}
            </StyledNav>
            {tableProps.canLoadMore}
            {isLoading && <Loading />}
            {data && (
                <PropertyGrid>
                    <PropertyInfo
                        propertyData={data}
                        updateLeadStatus={updateLeadStatus}
                        reset={reset}
                    />
                    <ExpectedDecisionMaker edmsData={data.edms} />
                    <TimelineContainer timelineData={data.timeline} />
                </PropertyGrid>
            )}
        </Container>
    );
};
